import { dollars } from "./money";
import { computeMatches, inferOpenGaps, sourceRowsFor } from "./matchEngine";
import type { BuyerFundingReview, FundingPurpose, OpenGap, PaymentRail, ReviewEvent } from "./types";

export const BUYER_FUNDING_PROCESS_BOUNDARY =
  "This receipt records the buyer funding instruction process. The office decides.";

export const BF001 = {
  reviewId: "bf-001",
  conflictReviewId: "bf-001-conflict",
  reviewNumber: "BFR-BF-001",
  conflictReviewNumber: "BFR-BF-001-CONFLICT",
  fileCode: "BF-001",
  office: "Sample Escrow Office",
  buyerName: "Jordan Avery",
  buyerEmail: "jordan.avery@example.com",
  buyerLastName: "Avery",
  property: "123 Sample Street, Long Beach, CA",
  amountDue: dollars(184217.42),
  amountDueLookup: "184217.42",
  bank: "Sample Bank",
  beneficiary: "Sample Escrow Trust Account",
  routingLast4: "0020",
  destinationAccountLast4: "7316",
  sourceAccountLast4: "8844",
  attackerAccountLast4: "4422",
  attackerRoutingLast4: "9911",
  attackerSource: "email",
  instructionVersion: "1",
  knownEntryPoint: "/files/BF-001",
  acknowledgmentStatement: "I reviewed the current funding instructions for this file.",
  officePublishedAt: "2026-06-02T15:00:00.000Z",
  buyerViewedAt: "2026-06-02T15:08:00.000Z",
  buyerAcknowledgedAt: "2026-06-02T15:09:00.000Z",
  conflictReportedAt: "2026-06-02T15:11:00.000Z",
  deviceVerifiedAt: "2026-06-02T15:05:00.000Z",
  identityVerifiedAt: "2026-06-02T15:06:00.000Z",
  instructionsDownloadedAt: "2026-06-02T15:07:00.000Z"
} as const;

export type BuyerFundingFlowEvidence = {
  knownEntryPoint: string;
  officePublishedAt: string;
  invitePhoneMasked?: string;
  deviceVerifiedAt?: string;
  identityVerifiedAt?: string;
  personaInquiryId?: string;
  personaStatus?: string;
  instructionsDownloadedAt?: string;
  paymentRequestId?: string;
  paymentInitiatedAt?: string;
  paymentInitiationReference?: string;
  buyerViewedAt?: string;
  acknowledgmentStatement?: string;
  buyerAcknowledgedAt?: string;
  lookupFileCode?: string;
  lookupBuyerLastName?: string;
  lookupAmountDue?: string;
  conflictReport?: {
    source: string;
    attackerRoutingLast4: string;
    attackerAccountLast4: string;
    reportedAt: string;
    note: string;
  };
  addressValidationAt?: string;
  addressValidationPlaceId?: string;
  addressValidationVerdict?: string;
  instructionAckMethod?: "passkey" | "checkbox";
  instructionAckCredentialId?: string;
  docuSignEnvelopeId?: string;
  docuSignSignedAt?: string;
  docuSignInstructionHash?: string;
  docuSignDocumentHash?: string;
  docuSignSignerEmail?: string;
  docuSignSignedDocumentBase64?: string;
};

export type BuyerFundingFlowReview = BuyerFundingReview & {
  buyerFundingFlow: BuyerFundingFlowEvidence;
};

export function createOfficeReviewFromSession(
  session: import("../flows/buyerFunding/types").BuyerFundingFlowSession,
  reviewId: string,
  reviewNumber: string,
  officeId = "office-1"
): BuyerFundingFlowReview {
  const publishedAt = session.instructions.publishedAt || new Date().toISOString();
  const dueAt = session.instructions.deadlineAt || publishedAt;
  return finalizeFlowReview({
    id: reviewId,
    reviewNumber,
    officeId,
    officeName: session.officeName,
    fileNumber: session.fileCode,
    propertyAddress: session.propertyAddress,
    closingDate: session.closingDate,
    buyerParties: [
      {
        id: `${reviewId}-buyer`,
        name: session.buyerName,
        role: "buyer",
        expectedFundingParty: true
      }
    ],
    fundingPurpose: session.paymentRequest.purpose as FundingPurpose,
    expectedAmount: session.amountDue,
    dueAt,
    reviewReason: session.paymentRequest.purpose,
    status: "Ready for review",
    fundsStatus: "Not requested",
    expectedFunds: {
      amount: session.amountDue,
      purpose: session.paymentRequest.purpose as FundingPurpose,
      dueAt,
      sourceDocumentId: `${session.fileCode} expected funds`,
      sourceStatus: "final",
      preparedBy: session.paymentRequest.requestedBy,
      extractionConfidence: 1
    },
    fundingInstruction: {
      versionLabel: session.instructions.versionLabel,
      sourceType: "Office funding instruction",
      sentBy: session.paymentRequest.requestedBy,
      sentTo: session.buyerName,
      sentAt: publishedAt,
      deliveryChannel: "Known Veto entry point",
      receivingBankName: session.instructions.bankName,
      receivingAccountLast4: session.instructions.accountLast4,
      routingLast4: session.instructions.routingLast4,
      beneficiaryName: session.instructions.beneficiaryName,
      allowedRails: [session.instructions.paymentRail as PaymentRail],
      memoRequired: session.instructions.memoRequired,
      deadlineAt: session.instructions.deadlineAt,
      officeTemplateStatus: "yes",
      sourceDocumentId: `${session.fileCode} instruction v${session.instructions.versionLabel}`,
      hash: `sha256-${session.fileCode}-instruction-v${session.instructions.versionLabel}`
    },
    buyerAcknowledgment: {
      type: "Not obtained",
      partyName: session.buyerName,
      acknowledgedAmount: session.amountDue,
      acknowledgedInstructionVersion: undefined,
      acknowledgedRail: session.instructions.paymentRail as PaymentRail,
      channel: "Known Veto entry point",
      sourceDocumentId: "Buyer known-entry acknowledgment",
      recordedBy: "system",
      limitation: "Buyer acknowledgment is recorded only after the buyer uses the known Veto entry point."
    },
    fundingSource: {
      fundingPartyName: session.buyerName,
      relationshipToBuyer: "Self",
      partyType: "Buyer",
      sourceType: "buyer_personal",
      limitation: "Funding source is recorded when the buyer completes source evidence in the flow."
    },
    sourceAccountEvidence: {
      evidenceType: "Not recorded",
      provider: "manual",
      recordedAt: publishedAt,
      ownerMatchResult: "Not recorded",
      limitation: "Source account evidence is recorded when the buyer completes the flow."
    },
    paymentRail: {
      rail: session.instructions.paymentRail as PaymentRail,
      allowedByInstructions: "yes",
      allowedByPolicy: "yes",
      expectedArrivalTiming: "Same business day",
      returnRiskNote: "No inbound funds are moved by Veto.",
      buyerFacingDeadline: session.instructions.deadlineAt,
      officeCutoff: session.instructions.deadlineAt
    },
    paymentInitiation: {
      evidenceType: "Not initiated",
      rail: session.instructions.paymentRail as PaymentRail,
      initiatedAmount: undefined,
      initiatedBy: session.buyerName,
      destinationShown: `${session.instructions.bankName} account ending ${session.instructions.accountLast4}`,
      reference: session.fileCode,
      fileMemo: session.fileCode,
      sourceDocumentId: "No payment initiation yet",
      limitation: "Payment initiation is recorded when the buyer reports funds sent."
    },
    inboundReceipt: {
      sourceType: "not_received",
      amountReceived: undefined,
      receivingAccountLast4: session.instructions.accountLast4,
      senderShown: session.buyerName,
      rail: session.instructions.paymentRail as PaymentRail,
      reference: session.fileCode,
      fileNumber: session.fileCode,
      returnRiskStatus: "unknown",
      accountingReviewer: "Pending"
    },
    matches: [],
    openGaps: [],
    officeAction: {
      action: "No action taken",
      fundsStatus: "Not requested",
      note: `Office published instruction version ${session.instructions.versionLabel} for buyer review.`,
      recordedBy: session.paymentRequest.requestedBy,
      recordedAt: publishedAt
    },
    signoff: {
      signerName: session.paymentRequest.requestedBy,
      role: "Escrow officer",
      office: session.officeName,
      authenticationMethod: "Cloudflare Access",
      signedAt: undefined,
      fundsStatusAtSignoff: "Not requested",
      gapsAcknowledged: false,
      officeAction: "No action taken",
      statementVersion: "bfr-flow-v1"
    },
    sourceRows: [],
    events: [],
    saveBackLocation: `Escrow file ${session.fileCode}`,
    receiptVersion: "v1",
    createdBy: session.paymentRequest.requestedBy,
    createdAt: publishedAt,
    updatedAt: publishedAt,
    buyerFundingFlow: {
      knownEntryPoint: `/files/${session.fileCode}`,
      officePublishedAt: publishedAt
    }
  });
}

export function createBF001OfficeReview(): BuyerFundingFlowReview {
  const session = {
    fileCode: BF001.fileCode,
    officeName: BF001.office,
    buyerName: BF001.buyerName,
    buyerLastName: BF001.buyerLastName,
    propertyAddress: BF001.property,
    amountDue: BF001.amountDue,
    amountDueLookup: BF001.amountDueLookup,
    paymentRequest: {
      requestId: "pay-req-bf-001",
      purpose: "Cash to close",
      requestedAt: BF001.officePublishedAt,
      requestedBy: BF001.office
    },
    instructions: {
      versionLabel: BF001.instructionVersion,
      publishedAt: BF001.officePublishedAt,
      paymentRail: "Wire",
      deadlineAt: "2026-06-06T21:00:00.000Z",
      beneficiaryName: BF001.beneficiary,
      bankName: BF001.bank,
      routingLast4: BF001.routingLast4,
      accountLast4: BF001.destinationAccountLast4,
      memoRequired: BF001.fileCode
    }
  } as import("../flows/buyerFunding/types").BuyerFundingFlowSession;
  const base = createOfficeReviewFromSession(session, BF001.reviewId, BF001.reviewNumber, "sample-office");
  return finalizeFlowReview({
    ...base,
    sourceAccountEvidence: {
      evidenceType: "Plaid account owner evidence",
      provider: "plaid",
      recordedAt: BF001.identityVerifiedAt,
      bankName: BF001.bank,
      accountLast4: BF001.sourceAccountLast4,
      accountType: "Checking",
      ownerName: BF001.buyerName,
      ownerMatchResult: "Matched",
      sourceParty: BF001.buyerName,
      limitation:
        "Plaid-linked source account evidence for the BF-001 smoke path. Evidence for review only; not a funds guarantee."
    },
    buyerFundingFlow: {
      knownEntryPoint: BF001.knownEntryPoint,
      officePublishedAt: BF001.officePublishedAt,
      deviceVerifiedAt: BF001.deviceVerifiedAt,
      identityVerifiedAt: BF001.identityVerifiedAt,
      instructionsDownloadedAt: BF001.instructionsDownloadedAt
    }
  });
}

export function createBF001AcknowledgedReview(): BuyerFundingFlowReview {
  return acknowledgeBF001(createBF001OfficeReview());
}

export function createBF001ConflictReview(
  source = BF001.attackerSource,
  attackerRoutingLast4 = BF001.attackerRoutingLast4,
  attackerAccountLast4 = BF001.attackerAccountLast4
): BuyerFundingFlowReview {
  return reportBF001Conflict(createBF001AcknowledgedReview(), source, attackerRoutingLast4, attackerAccountLast4);
}

export function buyerLookupMatches(fileCode: string, buyerLastName: string, amountDue: string): boolean {
  return (
    fileCode.trim().toUpperCase() === BF001.fileCode &&
    buyerLastName.trim().toLowerCase() === BF001.buyerLastName.toLowerCase() &&
    normalizeAmount(amountDue) === BF001.amountDueLookup
  );
}

export function acknowledgeFlowReview(
  review: BuyerFundingFlowReview,
  session: import("../flows/buyerFunding/types").BuyerFundingFlowSession
): BuyerFundingFlowReview {
  const acknowledgedAt =
    session.acknowledgedAt || session.instructionAck?.acknowledgedAt || new Date().toISOString();
  return finalizeFlowReview({
    ...review,
    buyerAcknowledgment: {
      ...review.buyerAcknowledgment,
      type: "Known-entry acknowledgment",
      acknowledgedInstructionVersion: session.instructions.versionLabel,
      acknowledgedAt,
      limitation: "Buyer acknowledgment records the statement shown from the known Veto entry point."
    },
    officeAction: {
      ...review.officeAction,
      action: "Accepted for file",
      note: "Buyer acknowledgment recorded from the known Veto entry point.",
      recordedAt: acknowledgedAt
    },
    status: "Recorded",
    updatedAt: acknowledgedAt,
    buyerFundingFlow: {
      ...review.buyerFundingFlow,
      lookupFileCode: session.fileCode,
      lookupBuyerLastName: session.buyerLastName,
      lookupAmountDue: session.amountDueLookup,
      buyerViewedAt: session.instructionsDownloadedAt,
      acknowledgmentStatement: "I reviewed the current funding instructions for this file.",
      buyerAcknowledgedAt: acknowledgedAt
    }
  });
}

export function reportFlowConflict(
  review: BuyerFundingFlowReview,
  session: import("../flows/buyerFunding/types").BuyerFundingFlowSession,
  source: string,
  attackerRoutingLast4: string,
  attackerAccountLast4: string
): BuyerFundingFlowReview {
  const reportedAt = new Date().toISOString();
  const conflictGap: OpenGap = {
    id: `${review.id}-gap-conflict`,
    reviewId: review.id,
    gapType: "Instruction mismatch",
    severity: "stop_hold",
    description: "Buyer reported conflicting instructions from email after viewing Veto instructions.",
    sourceRowIds: [`${review.id}-src-conflict-report`],
    officeActionRequired: true,
    exceptionRequired: true,
    reviewerNote: "Hold file while office reviews conflicting instructions report.",
    acknowledgedBy: session.buyerName,
    acknowledgedAt: reportedAt,
    status: "retained"
  };

  return finalizeFlowReview({
    ...review,
    status: "Hold / conflicting instructions reported",
    fundsStatus: "Held",
    openGaps: [conflictGap],
    officeAction: {
      ...review.officeAction,
      action: "Held pending review",
      fundsStatus: "Held",
      note: "Hold / conflicting instructions reported.",
      recordedAt: reportedAt
    },
    signoff: {
      ...review.signoff,
      officeAction: "Held pending review",
      fundsStatusAtSignoff: "Held",
      signedAt: reportedAt
    },
    updatedAt: reportedAt,
    buyerFundingFlow: {
      ...review.buyerFundingFlow,
      lookupFileCode: session.fileCode,
      lookupBuyerLastName: session.buyerLastName,
      lookupAmountDue: session.amountDueLookup,
      buyerViewedAt: session.instructionsDownloadedAt,
      conflictReport: {
        source,
        attackerRoutingLast4,
        attackerAccountLast4,
        reportedAt,
        note: "Buyer reported conflicting instructions from email."
      }
    }
  });
}

export function acknowledgeBF001(review: BuyerFundingFlowReview): BuyerFundingFlowReview {
  return finalizeFlowReview({
    ...review,
    buyerAcknowledgment: {
      ...review.buyerAcknowledgment,
      type: "Known-entry acknowledgment",
      acknowledgedInstructionVersion: BF001.instructionVersion,
      acknowledgedAt: BF001.buyerAcknowledgedAt,
      limitation: "Buyer acknowledgment records the statement shown from the known Veto entry point."
    },
    officeAction: {
      ...review.officeAction,
      action: "Accepted for file",
      note: "Buyer acknowledgment recorded from the known Veto entry point.",
      recordedAt: BF001.buyerAcknowledgedAt
    },
    signoff: {
      ...review.signoff,
      officeAction: "Accepted for file",
      signedAt: "2026-06-02T15:22:00.000Z"
    },
    status: "Recorded",
    updatedAt: BF001.buyerAcknowledgedAt,
    buyerFundingFlow: {
      ...review.buyerFundingFlow,
      lookupFileCode: BF001.fileCode,
      lookupBuyerLastName: BF001.buyerLastName,
      lookupAmountDue: BF001.amountDueLookup,
      buyerViewedAt: BF001.buyerViewedAt,
      acknowledgmentStatement: BF001.acknowledgmentStatement,
      buyerAcknowledgedAt: BF001.buyerAcknowledgedAt
    }
  });
}

export function reportBF001Conflict(
  review: BuyerFundingFlowReview,
  source: string,
  attackerRoutingLast4: string,
  attackerAccountLast4: string
): BuyerFundingFlowReview {
  const conflictGap: OpenGap = {
    id: `${BF001.conflictReviewId}-gap-conflict`,
    reviewId: BF001.conflictReviewId,
    gapType: "Instruction mismatch",
    severity: "stop_hold",
    description: "Buyer reported conflicting instructions from email after viewing Veto instructions.",
    sourceRowIds: [`${BF001.conflictReviewId}-src-conflict-report`],
    officeActionRequired: true,
    exceptionRequired: true,
    reviewerNote: "Hold file while office reviews conflicting instructions report.",
    acknowledgedBy: BF001.buyerName,
    acknowledgedAt: BF001.conflictReportedAt,
    status: "retained"
  };

  return finalizeFlowReview({
    ...review,
    id: BF001.conflictReviewId,
    reviewNumber: BF001.conflictReviewNumber,
    status: "Hold / conflicting instructions reported",
    fundsStatus: "Held",
    openGaps: [conflictGap],
    officeAction: {
      ...review.officeAction,
      action: "Held pending review",
      fundsStatus: "Held",
      note: "Hold / conflicting instructions reported.",
      recordedAt: BF001.conflictReportedAt
    },
    signoff: {
      ...review.signoff,
      officeAction: "Held pending review",
      fundsStatusAtSignoff: "Held",
      signedAt: "2026-06-02T15:24:00.000Z"
    },
    updatedAt: BF001.conflictReportedAt,
    buyerFundingFlow: {
      ...review.buyerFundingFlow,
      lookupFileCode: BF001.fileCode,
      lookupBuyerLastName: BF001.buyerLastName,
      lookupAmountDue: BF001.amountDueLookup,
      buyerViewedAt: BF001.buyerViewedAt,
      conflictReport: {
        source,
        attackerRoutingLast4,
        attackerAccountLast4,
        reportedAt: BF001.conflictReportedAt,
        note: "Buyer reported conflicting instructions from email."
      }
    }
  });
}

export function flowEvidenceFor(review: BuyerFundingReview): BuyerFundingFlowEvidence | undefined {
  return (review as Partial<BuyerFundingFlowReview>).buyerFundingFlow;
}

function finalizeFlowReview(review: BuyerFundingFlowReview): BuyerFundingFlowReview {
  const sourceRows = sourceRowsFor(review);
  const matches = computeMatches({ ...review, sourceRows });
  const openGaps = inferOpenGaps({ ...review, sourceRows, matches });
  const merged = {
    ...review,
    sourceRows,
    matches,
    openGaps: mergeGaps(review.openGaps, openGaps)
  };
  return {
    ...merged,
    events: flowEventsFor(merged)
  };
}

function flowEventsFor(review: BuyerFundingFlowReview): ReviewEvent[] {
  const eventTypes = [
    "Office file created",
    "Funding instructions recorded",
    "Office reviewed buyer-facing instructions",
    "Instruction version published",
    "Buyer viewed instructions from known Veto entry point"
  ];
  if (review.buyerFundingFlow.buyerAcknowledgedAt) {
    eventTypes.push("Buyer acknowledgment recorded");
  }
  if (review.buyerFundingFlow.conflictReport) {
    eventTypes.push("Conflicting instructions report recorded");
    eventTypes.push("Office action recorded");
  }
  eventTypes.push("Receipt exported");

  return eventTypes.map((eventType, index) => ({
    id: `${review.id}-flow-event-${index + 1}`,
    reviewId: review.id,
    actorId: eventType.startsWith("Buyer") || eventType.startsWith("Conflicting") ? BF001.buyerName : "Sample Escrow Officer",
    eventType,
    occurredAt: new Date(new Date(review.createdAt).getTime() + index * 60000).toISOString(),
    objectType: "Buyer Funding Review flow smoke",
    sourceRowIds: sourceRowsForFlowEvent(review.id, eventType)
  }));
}

function sourceRowsForFlowEvent(reviewId: string, eventType: string): string[] {
  const lower = eventType.toLowerCase();
  if (lower.includes("published") || lower.includes("instruction")) return [`${reviewId}-src-instruction`];
  if (lower.includes("viewed") || lower.includes("known")) return [`${reviewId}-src-buyer-entry`];
  if (lower.includes("acknowledgment")) return [`${reviewId}-src-ack`, `${reviewId}-src-buyer-entry`];
  if (lower.includes("conflicting")) return [`${reviewId}-src-conflict-report`];
  if (lower.includes("office action")) return [`${reviewId}-src-office-action`, `${reviewId}-src-conflict-report`];
  if (lower.includes("export")) return [`${reviewId}-src-export`];
  return [`${reviewId}-src-expected`];
}

function mergeGaps(existing: OpenGap[], inferred: OpenGap[]): OpenGap[] {
  const byKey = new Map<string, OpenGap>();
  for (const gap of [...existing, ...inferred]) {
    byKey.set(`${gap.gapType}:${gap.description}`, gap);
  }
  return Array.from(byKey.values());
}

function normalizeAmount(value: string): string {
  const parsed = Number.parseFloat(value.replace(/[^0-9.]/g, ""));
  return Number.isFinite(parsed) ? parsed.toFixed(2) : "";
}
